import styled, { css } from 'styled-components';
import {
  AppAudioPlayerControlsPauseFilled,
  AppAudioPlayerControlsPlayFilled,
  AppAudioPlayerControlsPlayFilledDisabled
} from '@elfsight/icons';
import { getColorWithAlpha } from '@elfsight/widget-utils';
import type { PropsWithClassName } from '../../../../../types';
import { useTheme } from '../../../common';
import { usePlayer } from '../../PlayerProvider';
import { DefaultButton } from './DefaultButton';
import { Icon } from './Icon';

interface PlayProps extends PropsWithClassName {
  disabled?: boolean;
}

export function PlayPause({ className, disabled = false }: PlayProps) {
  const { textColor } = useTheme();
  const { togglePlaying, playing, hasInteracted } = usePlayer();

  const onClickHandler = () => {
    if ((!hasInteracted && playing) || disabled) {
      return;
    }

    togglePlaying(!playing);
  };

  const derivedIcon = (() => {
    if (disabled) {
      return (
        <Icon
          as={AppAudioPlayerControlsPlayFilledDisabled}
          fill={getColorWithAlpha(textColor, 0.3)}
        />
      );
    }

    return (
      <Icon
        as={
          playing
            ? AppAudioPlayerControlsPauseFilled
            : AppAudioPlayerControlsPlayFilled
        }
        fill={textColor}
      />
    );
  })();

  return (
    <Component
      aria-label={playing ? 'Pause' : 'Play'}
      className={className}
      onClick={onClickHandler}
      _playing={playing}
      _disabled={disabled}
    >
      {derivedIcon}
    </Component>
  );
}

const Component = styled(DefaultButton).attrs<{
  _disabled: boolean;
  _playing: boolean;
}>(({ _playing }) => ({
  className: _playing ? 'Pause__Component-sc' : 'Play__Component-sc'
}))`
  width: 36px !important;
  height: 36px !important;
  flex-basis: 36px;

  ${({ _disabled }) =>
    _disabled &&
    css`
      pointer-events: none;
    `};

  &:before {
    width: 36px;
    height: 36px;
  }
`;
